import { ApiClientInstance } from '@/_/5.Shared/api';
import { deleteData, getData, getFile, postData, putData } from '@/lib/utils/api';
import { IResponse } from '@/models';
import {
  IAuthResponse,
  IAddMobile,
  IAuthRequest,
  IConfirmAddMobileOtp,
  IIndividualCustomer,
  IProfileAddresses,
  IProfileEmails,
  IProfileMobiles,
  ISejamInfo,
  IProfileSecurityLogs,
  IChangeUserName,
  IConfirmUsername,
  ITwoStepVerificationConfirm,
  ISejamConfirm,
  IProfileContract,
  IContract,
  IProfileContractSignData,
  IContractPhoneNumber,
  ContractPreviewType,
  IprofileSecurity,
  IOtpResponse,
  IConfirmPassword,
} from '@/models/profiles';

export const sejamInformation = (): Promise<ISejamInfo> => {
  return getData(`profile/userinfo/sejam`, { version: 'v2' });
};

export const getCustomerInfo = (): Promise<IIndividualCustomer> => {
  return ApiClientInstance.get<IIndividualCustomer>(`v2/profile/userinfo`);
};

export const postSejamOtp = (): Promise<IOtpResponse> => {
  return postData<void, IOtpResponse>(`profile/userinfo/sejam/otp-request`, { version: 'v2' });
};
export const postConfirmSejamOtp = (data: ISejamConfirm): Promise<void> => {
  return postData<ISejamConfirm, void>(`profile/userinfo/sejam`, {
    data,
    version: 'v2',
  });
};

export const getProfileMobiles = (ownerStatus?: number): Promise<IProfileMobiles> => {
  const params = ownerStatus !== undefined ? `?ownerStatus=${ownerStatus}` : '';
  return getData(`profile/contacts/phone-numbers${params}`, { version: 'v2' });
};

export const getSessionLogs = (): Promise<IProfileSecurityLogs> => {
  return getData(`profile/security/sessions`, { version: 'v2' });
};

export const getSessionLogsExportExcel = (): Promise<Blob> => {
  return getData(`profile/security/sessions/export`, { responseType: 'blob', version: 'v2' });
};

export const getProfileEmails = (): Promise<IProfileEmails> => {
  return getData(`profile/contacts/emails`, { version: 'v2' });
};

export const getProfileAddresses = (): Promise<IProfileAddresses> => {
  return getData(`profile/contacts/addresses`, { version: 'v2' });
};
export const postAddMobile = (data: IAddMobile): Promise<IOtpResponse> => {
  return postData<string, IOtpResponse>(`profile/contacts/phone-numbers/otp-request`, {
    data: data.phoneNumber,
    version: 'v2',
  });
};

export const postConfirmAddMobileOtp = (data: IConfirmAddMobileOtp): Promise<void> => {
  return postData<IConfirmAddMobileOtp, void>(`profile/contacts/phone-numbers`, {
    data: data,
    version: 'v2',
  });
};

export const profileSecurity = (): Promise<IprofileSecurity> => {
  return getData<IprofileSecurity>(`profile/security`, { version: 'v2' });
};

export const ChangeUsernameOtp = (data: IChangeUserName): Promise<IOtpResponse> => {
  return postData<string, IOtpResponse>(`profile/security/username/otp-request`, {
    data: data.username,
    version: 'v2',
  });
};

export const ConfirmChangeUsername = (data: IConfirmUsername): Promise<void> => {
  return putData<IConfirmUsername, void>(`profile/security/username`, {
    data: data,
    version: 'v2',
  });
};

export const ChangePasswordOtp = (): Promise<IOtpResponse> => {
  return postData<void, IOtpResponse>(`profile/security/password/otp-request`, {
    version: 'v2',
  });
};

export const ConfirmPassword = (data: IConfirmPassword): Promise<void> => {
  return putData<IConfirmPassword, void>(`profile/security/password`, {
    data: data,
    version: 'v2',
  });
};

export const postActivateTwoStepVerification = (): Promise<void> => {
  return postData<void, void>(`profile/security/2fa`, { version: 'v2' });
};

export const requestTwoStepVerificationOtp = ({
  isActivate,
}: {
  isActivate: boolean;
}): Promise<IOtpResponse> => {
  return postData<void, IOtpResponse>(`profile/security/2fa/otp-request?IsActivate=${isActivate}`, {
    version: 'v2',
  });
};

export const deactivateTwoStepVerification = (data: ITwoStepVerificationConfirm): Promise<void> => {
  return deleteData<string, void>(`profile/security/2fa`, {
    data: JSON.stringify(data.otp),
    version: 'v2',
  });
};

export const activateTwoStepVerification = (data: ITwoStepVerificationConfirm): Promise<void> => {
  return postData<string, void>(`profile/security/2fa`, {
    data: JSON.stringify(data.otp),
    version: 'v2',
  });
};

export const getProfileContract = (): Promise<IProfileContract> => {
  return getData<IProfileContract>(`profile/contracts`, { version: 'v2' });
};

export const getContractByTypeCode = (
  contractTypeCodes: number[],
  previewType: ContractPreviewType
): Promise<IContract> => {
  const query = contractTypeCodes
    .map((code) => `contractTypeCodes=${encodeURIComponent(code)}`)
    .join('&');

  const url = `profile/contracts/preview/${previewType}?${query}`;

  return getData<IContract>(url, {
    version: 'v2',
  });
};

export const getSignedContractById = (contractId: number) => {
  return getFile(`profile/contracts/${contractId}/file`, { version: 'v2' });
};

export const deleteMobile = (contactCode: string): Promise<void> => {
  return deleteData<void, void>(`profile/contacts/phone-numbers/${contactCode}`, {
    version: 'v2',
  });
};
export const postDefaultContactCode = (contactCode: string): Promise<void> => {
  return putData<string, void>(`profile/contacts/phone-numbers/${contactCode}/default`, {
    version: 'v2',
  });
};
export const postProfileContractOtp = (contractTypeCodes: number[]): Promise<IOtpResponse> => {
  return postData<number[], IOtpResponse>(`profile/contracts/otp-request`, {
    version: 'v2',
    data: contractTypeCodes,
  });
};
export const getContractPhoneNumber = (): Promise<IContractPhoneNumber> => {
  return getData<IContractPhoneNumber>(`profile/contracts/user-phone-number`, { version: 'v2' });
};

export const postProfileContractSign = (data: IProfileContractSignData): Promise<void> => {
  return postData<IProfileContractSignData, void>(`profile/contracts/sign`, {
    data: data,
    version: 'v2',
  });
};

export const postAccessToken = (data: IAuthRequest): Promise<IAuthResponse> => {
  return postData<IAuthRequest, IAuthResponse>(`auth`, { data: data });
};

export const unAuthorize = (): Promise<IResponse> => {
  return getData<IResponse>(`auth/logout`);
};
